
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, CircleHelp } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Calendar } from 'lucide-react';

export type TimePeriod = 'day' | 'week' | 'month' | 'year' | 'all';

export function TrainingJournalHeader({
  selectedPeriod,
  onPeriodChange,
  onAddSession
}: {
  selectedPeriod?: TimePeriod;
  onPeriodChange?: (period: TimePeriod) => void;
  onAddSession: () => void;
}) {
  const isMobile = useIsMobile();
  const [helpOpen, setHelpOpen] = useState(false);

  const helpContent = {
    title: "Training Journal Help",
    description: "Track and analyze your training progress",
    content: (
      <>
        <p>Welcome to your Training Journal! Here you can:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Record your training sessions</li>
          <li>Track techniques you've practiced</li>
          <li>View statistics about your training</li>
          <li>Filter your sessions by time period</li>
        </ul>
        <p>Regularly logging your training helps you identify patterns and track your progress over time.</p>
      </>
    )
  };

  if (isMobile) {
    return (
      <div className="flex justify-center mb-4">
        <Button
          variant="default"
          size="sm"
          onClick={onAddSession}
          className="shadow-sm"
        >
          <Plus className="h-4 w-4 mr-1" />
          Log Training
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl sm:text-3xl font-bold inline-flex items-center gap-2">
          Training Journal
          <CircleHelp 
            className="h-5 w-5 text-primary cursor-pointer hover:text-primary/80 transition-colors" 
            onClick={() => setHelpOpen(true)}
            strokeWidth={2} 
          />
        </h1>
      </div>

      {/* Help Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{helpContent.title}</DialogTitle>
            <DialogDescription>
              {helpContent.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {helpContent.content}
          </div>
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button variant="default" size="sm">Got it</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
      
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <Select value={selectedPeriod} onValueChange={value => onPeriodChange?.(value as TimePeriod)}>
            <SelectTrigger className="h-8 w-[130px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Button
          variant="default"
          size="sm"
          onClick={onAddSession}
          className="shadow-sm"
        >
          <Plus className="h-4 w-4 mr-1" />
          Log Training
        </Button>
      </div>
    </div>
  );
}
